import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  type AfterViewInit,
  type ElementRef,
  type OnDestroy,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroBold,
  heroCodeBracket,
  heroH1,
  heroH2,
  heroH3,
  heroItalic,
  heroListBullet,
  heroNumberedList,
  heroPlus,
  heroSparkles,
} from '@ng-icons/heroicons/outline';
import Quill, { Delta, type EmitterSource } from 'quill';
import type { AgentMissionDocument } from '../../../core/api/generated/models/AgentMissionDocument';
import type { AgentMissionReference } from '../../../core/api/generated/models/AgentMissionReference';
import { UiButton, UiOptionRow, UiPanelCard } from '../../../shared/ui';
import type { BuilderMissionMention } from './agent-builder.types';
import {
  missionChipValueFromMention,
  missionChipValueFromRawToken,
  missionCommandTriggerFromText,
  missionDeltaFromDocument,
  missionDocumentFromDelta,
  missionDocumentText,
  registerMissionQuillBlots,
  sanitizeMissionDelta,
  type MissionChipValue,
  type MissionEditorSnapshot,
} from './agent-mission-quill';

type MissionSuggestionOption = {
  id: string;
  label: string;
  tokenLabel: string;
  description: string;
  mention?: BuilderMissionMention;
  fieldDraft?: MissionChipValue;
  disabled?: boolean;
};

type MissionSuggestionState = {
  from: number;
  to: number;
  left: number;
  top: number;
  activeIndex: number;
  options: readonly MissionSuggestionOption[];
};

const TYPED_RAW_TOKEN_PATTERN =
  /(^|\s)((?:!tool|@library|@skill|>automation|\$field):[a-zA-Z0-9_-]+(?:[._:-][a-zA-Z0-9_-]+)*)\s$/u;

@Component({
  selector: 'app-agent-mission-quill-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, UiButton, UiOptionRow, UiPanelCard],
  providers: [
    provideIcons({
      heroArrowPath,
      heroBold,
      heroCodeBracket,
      heroH1,
      heroH2,
      heroH3,
      heroItalic,
      heroListBullet,
      heroNumberedList,
      heroPlus,
      heroSparkles,
    }),
  ],
  template: `
    <section #shell class="quill-shell" aria-label="Editor da missão">
      <div appUiPanelCard class="quill-toolbar" aria-label="Controles do editor">
        <div class="toolbar-group toolbar-group--primary">
          <button
            appButton
            variant="secondary"
            size="sm"
            type="button"
            title="Adicionar etapa"
            aria-label="Adicionar etapa"
            (click)="insertStepSection()"
          >
            <ng-icon name="heroPlus" size="14" aria-hidden="true" />
            Etapa
          </button>
        </div>

        <div class="toolbar-group">
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Título principal"
            aria-label="Título principal"
            (click)="toggleHeading(1)"
          >
            <ng-icon name="heroH1" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Título de etapa"
            aria-label="Título de etapa"
            (click)="toggleHeading(2)"
          >
            <ng-icon name="heroH2" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Subtítulo"
            aria-label="Subtítulo"
            (click)="toggleHeading(3)"
          >
            <ng-icon name="heroH3" size="14" aria-hidden="true" />
          </button>
        </div>

        <div class="toolbar-group">
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Negrito"
            aria-label="Negrito"
            (click)="toggleTextFormat('bold')"
          >
            <ng-icon name="heroBold" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Itálico"
            aria-label="Itálico"
            (click)="toggleTextFormat('italic')"
          >
            <ng-icon name="heroItalic" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Lista"
            aria-label="Lista"
            (click)="toggleList('bullet')"
          >
            <ng-icon name="heroListBullet" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Passos numerados"
            aria-label="Passos numerados"
            (click)="toggleList('ordered')"
          >
            <ng-icon name="heroNumberedList" size="14" aria-hidden="true" />
          </button>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            title="Bloco de código"
            aria-label="Bloco de código"
            (click)="toggleCodeBlock()"
          >
            <ng-icon name="heroCodeBracket" size="14" aria-hidden="true" />
          </button>
        </div>

        <span class="toolbar-spacer" aria-hidden="true"></span>

        <button
          appButton
          class="toolbar-generate"
          variant="secondary"
          size="sm"
          type="button"
          title="Gerar com IA"
          aria-label="Gerar missão com IA"
          (click)="generateRequested.emit()"
          [disabled]="generateDisabled()"
        >
          <ng-icon
            [name]="generating() ? 'heroArrowPath' : 'heroSparkles'"
            size="14"
            aria-hidden="true"
          />
          {{ generating() ? 'Gerando' : hasGeneratedMission() ? 'Regerar' : 'Gerar com IA' }}
        </button>
      </div>

      <div #editorHost class="quill-editor" aria-label="Texto da missão"></div>

      @if (suggestion(); as menu) {
        <div
          appUiPanelCard
          class="quill-suggestion"
          role="listbox"
          aria-label="Sugestões do editor"
          [attr.aria-activedescendant]="suggestionOptionId(menu.activeIndex)"
          [style.left.px]="menu.left"
          [style.top.px]="menu.top"
        >
          @for (option of menu.options; track option.id; let index = $index) {
            <button
              appUiOptionRow
              compact
              type="button"
              class="quill-suggestion__option"
              [id]="suggestionOptionId(index)"
              [selected]="index === menu.activeIndex"
              [class.quill-suggestion__option--active]="index === menu.activeIndex"
              role="option"
              [attr.aria-selected]="index === menu.activeIndex"
              [attr.aria-disabled]="option.disabled ? 'true' : null"
              [disabled]="option.disabled"
              (mousedown)="$event.preventDefault()"
              (click)="applySuggestion(option)"
            >
              <span class="quill-suggestion__token">{{ option.tokenLabel }}</span>
              <span>
                <strong>{{ option.label }}</strong>
                <small>{{ option.description }}</small>
              </span>
            </button>
          }
        </div>
      }
    </section>
  `,
  styles: [
    `
      .quill-shell {
        position: relative;
        display: grid;
        min-width: 0;
        gap: 0.9rem;
      }

      .quill-toolbar {
        position: sticky;
        top: 0.75rem;
        z-index: 4;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.38rem;
        padding: 0.32rem;
        backdrop-filter: blur(0.9rem);
      }

      .toolbar-spacer {
        flex: 1 1 auto;
        min-width: 0.6rem;
      }

      .toolbar-generate {
        margin-left: auto;
      }

      .toolbar-group {
        display: inline-flex;
        align-items: center;
        gap: 0.12rem;
        border-right: 1px solid color-mix(in oklch, var(--color-line) 74%, transparent);
        padding-right: 0.35rem;
      }

      .toolbar-group:last-child {
        border-right: 0;
        padding-right: 0;
      }

      .toolbar-group--primary {
        flex-wrap: wrap;
        padding-right: 0.45rem;
      }

      .toolbar-action {
        color: var(--color-muted);
      }

      .quill-editor {
        min-width: 0;
      }

      :host ::ng-deep .ql-container {
        min-width: 0;
        font-family: inherit;
      }

      :host ::ng-deep .ql-editor {
        min-height: 35rem;
        outline: none;
        color: var(--color-ink);
        caret-color: var(--color-accent);
        padding: clamp(0.25rem, 1vw, 0.6rem);
        white-space: pre-wrap;
      }

      :host ::ng-deep .ql-editor > * {
        position: relative;
        min-width: 0;
        margin: 0;
        border-radius: var(--radius-sm);
        padding: 0.16rem 0.35rem;
        overflow-wrap: anywhere;
      }

      :host ::ng-deep .ql-editor > * + * {
        margin-top: 0.35rem;
      }

      :host ::ng-deep .ql-editor > *:hover {
        background: color-mix(in oklch, var(--color-soft) 54%, transparent);
      }

      :host ::ng-deep .ql-editor h1 {
        margin-bottom: 0.75rem;
        color: var(--color-ink);
        font-size: clamp(1.45rem, 2.4vw, 2rem);
        font-weight: 820;
        line-height: 1.1;
      }

      :host ::ng-deep .ql-editor h2 {
        margin-top: 1rem;
        color: var(--color-ink);
        font-size: 1.04rem;
        font-weight: 800;
        line-height: 1.35;
      }

      :host ::ng-deep .ql-editor h3 {
        margin-top: 0.65rem;
        color: var(--color-ink);
        font-size: 0.94rem;
        font-weight: 780;
        line-height: 1.38;
      }

      :host ::ng-deep .ql-editor p,
      :host ::ng-deep .ql-editor li {
        color: var(--color-ink);
        font-size: 0.94rem;
        font-weight: 520;
        line-height: 1.68;
      }

      :host ::ng-deep .ql-editor.ql-blank::before {
        left: 0.95rem;
        color: color-mix(in oklch, var(--color-muted) 72%, transparent);
        font-style: normal;
      }

      :host ::ng-deep .ql-editor ul,
      :host ::ng-deep .ql-editor ol {
        padding-left: 1.35rem;
      }

      :host ::ng-deep .ql-code-block-container,
      :host ::ng-deep .ql-code-block {
        border-radius: var(--radius-sm);
        background: color-mix(in oklch, var(--color-ink) 6%, var(--color-panel));
        color: var(--color-ink);
        font-family:
          ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
        font-size: 0.82rem;
        line-height: 1.55;
      }

      :host ::ng-deep .ql-code-block {
        padding: 0.12rem 0.45rem;
      }

      :host ::ng-deep .mission-token {
        display: inline-flex;
        max-width: min(24rem, 100%);
        align-items: center;
        min-height: 1.55rem;
        vertical-align: baseline;
        border-radius: var(--radius-pill);
        background: color-mix(in oklch, var(--color-accent) 12%, transparent);
        color: var(--color-accent-strong);
        padding: 0 0.48rem;
        font-size: 0.78rem;
        font-weight: 780;
        line-height: 1;
        white-space: nowrap;
      }

      :host ::ng-deep .mission-token--tool,
      :host ::ng-deep .mission-token--trigger {
        background: color-mix(in oklch, var(--color-tomato) 12%, transparent);
        color: var(--color-tomato);
      }

      :host ::ng-deep .mission-token--automation,
      :host ::ng-deep .mission-token--stage {
        background: color-mix(in oklch, var(--color-accent-soft) 52%, transparent);
        color: var(--color-accent-strong);
      }

      :host ::ng-deep .mission-token--field {
        background: color-mix(in oklch, var(--color-accent) 9%, var(--color-soft));
        color: var(--color-accent-strong);
      }

      :host ::ng-deep .mission-token--handoff {
        background: color-mix(in oklch, var(--color-warning) 16%, transparent);
        color: var(--color-warning);
      }

      .quill-suggestion {
        position: absolute;
        z-index: 12;
        display: grid;
        width: min(23rem, calc(100% - 1rem));
        gap: 0.18rem;
        box-shadow: var(--shadow-panel);
        padding: 0.35rem;
        backdrop-filter: blur(1rem);
      }

      .quill-suggestion__option {
        display: grid;
        grid-template-columns: minmax(4.8rem, auto) minmax(0, 1fr);
        align-items: center;
        min-width: 0;
        gap: 0.55rem;
        border: 0;
        border-radius: var(--radius-xs);
        background: transparent;
        color: var(--color-ink);
        padding: 0.45rem 0.5rem;
        text-align: left;
      }

      .quill-suggestion__option:hover,
      .quill-suggestion__option:focus-visible,
      .quill-suggestion__option--active {
        background: color-mix(in oklch, var(--color-accent) 8%, transparent);
        outline: none;
      }

      .quill-suggestion__option:disabled {
        cursor: default;
        opacity: 0.78;
      }

      .quill-suggestion__token {
        overflow: hidden;
        color: var(--color-accent-strong);
        font-size: 0.76rem;
        font-weight: 820;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .quill-suggestion__option span:last-child {
        display: grid;
        min-width: 0;
        gap: 0.08rem;
      }

      .quill-suggestion__option strong,
      .quill-suggestion__option small {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .quill-suggestion__option strong {
        font-size: 0.8rem;
        font-weight: 780;
      }

      .quill-suggestion__option small {
        color: var(--color-muted);
        font-size: 0.72rem;
        font-weight: 620;
      }

      @media (max-width: 760px) {
        .quill-toolbar {
          top: 0.65rem;
        }

        .toolbar-group {
          border-right: 0;
        }

        .toolbar-spacer {
          display: none;
        }

        .toolbar-generate {
          margin-left: 0;
        }

        :host ::ng-deep .ql-editor {
          min-height: 22rem;
        }
      }
    `,
  ],
})
export class AgentMissionQuillEditorComponent implements AfterViewInit, OnDestroy {
  readonly document = input.required<AgentMissionDocument>();
  readonly mentionOptions = input<readonly BuilderMissionMention[]>([]);
  readonly generating = input(false);
  readonly hasGeneratedMission = input(false);
  readonly generateDisabled = input(false);
  readonly documentChange = output<MissionEditorSnapshot>();
  readonly generateRequested = output<void>();
  readonly suggestion = signal<MissionSuggestionState | null>(null);

  private readonly shell = viewChild.required<ElementRef<HTMLElement>>('shell');
  private readonly editorHost = viewChild.required<ElementRef<HTMLDivElement>>('editorHost');
  private readonly ready = signal(false);
  private quill: Quill | null = null;
  private lastDocumentText = '';
  private lastMentionSignature = '';
  private applyingExternalDocument = false;
  private suppressTextChange = false;

  private readonly textChangeHandler = (
    _delta: Delta,
    _oldContent: Delta,
    source: EmitterSource,
  ): void => {
    if (this.suppressTextChange) return;
    if (source !== Quill.sources.USER) {
      this.updateSuggestion();
      return;
    }
    this.applyMarkdownShortcut();
    if (source === Quill.sources.USER) this.replaceTrailingRawToken();
    this.emitSnapshot();
    queueMicrotask(() => this.updateSuggestion());
  };

  private readonly selectionChangeHandler = (): void => {
    this.updateSuggestion();
  };

  private readonly keydownHandler = (event: KeyboardEvent): void => {
    this.handleSuggestionKeydown(event);
  };

  constructor() {
    effect(() => {
      const document = this.document();
      const mentionSignature = mentionOptionsSignature(this.mentionOptions());
      if (!this.ready()) return;
      if (!this.quill) {
        this.createEditor(document);
        return;
      }
      const nextDocumentText = missionDocumentText(document);
      if (this.applyingExternalDocument) return;
      if (nextDocumentText === this.lastDocumentText) {
        this.lastMentionSignature = mentionSignature;
        return;
      }
      this.replaceDocument(document);
    });
  }

  ngAfterViewInit(): void {
    registerMissionQuillBlots();
    this.ready.set(true);
  }

  ngOnDestroy(): void {
    if (!this.quill) return;
    this.quill.off(Quill.events.TEXT_CHANGE, this.textChangeHandler);
    this.quill.off(Quill.events.SELECTION_CHANGE, this.selectionChangeHandler);
    this.quill.root.removeEventListener('keydown', this.keydownHandler, true);
    this.quill = null;
  }

  insertMention(mention: BuilderMissionMention): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    this.replaceRangeWithMention(range.index, range.index + range.length, mention);
  }

  insertCollectionField(mention: BuilderMissionMention): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const selection = quill.getSelection();
    const insertAt = selection?.index ?? Math.max(0, quill.getLength() - 1);
    const selectionLength = selection?.length ?? 0;
    const shouldAppendSentence = !selection;
    const fieldDelta = new Delta()
      .retain(insertAt)
      .delete(selectionLength);
    if (shouldAppendSentence) fieldDelta.insert('\nColetar ');
    fieldDelta
      .insert({ missionChip: missionChipValueFromMention(mention) })
      .insert(shouldAppendSentence ? ' nesta etapa antes de executar ações sensíveis.\n' : ' ');
    this.suppressTextChange = true;
    try {
      quill.updateContents(fieldDelta, Quill.sources.USER);
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(insertAt + fieldDelta.length(), 0, Quill.sources.SILENT);
    quill.focus();
    this.emitSnapshot();
  }

  insertStepSection(): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    this.replaceRangeWithStepSection(range.index, range.index + range.length);
  }

  toggleHeading(level: 1 | 2 | 3): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    const formats = quill.getFormat(range);
    quill.formatLine(
      range.index,
      Math.max(range.length, 1),
      'header',
      formats['header'] === level ? false : level,
      Quill.sources.USER,
    );
    quill.focus();
  }

  toggleCodeBlock(): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    const formats = quill.getFormat(range);
    quill.formatLine(
      range.index,
      Math.max(range.length, 1),
      'code-block',
      formats['code-block'] ? false : true,
      Quill.sources.USER,
    );
    quill.focus();
  }

  toggleTextFormat(format: 'bold' | 'italic'): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    const formats = quill.getFormat(range);
    quill.format(format, !formats[format], Quill.sources.USER);
    quill.focus();
  }

  toggleList(type: 'bullet' | 'ordered'): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const range = quill.getSelection(true);
    const formats = quill.getFormat(range);
    quill.formatLine(
      range.index,
      Math.max(range.length, 1),
      'list',
      formats['list'] === type ? false : type,
      Quill.sources.USER,
    );
    quill.focus();
  }

  applySuggestion(option: MissionSuggestionOption): void {
    if (option.disabled) return;
    const suggestion = this.suggestion();
    if (!suggestion) return;
    if (option.mention) {
      this.replaceRangeWithMention(suggestion.from, suggestion.to, option.mention);
    } else if (option.fieldDraft) {
      this.replaceRangeWithMissionChip(suggestion.from, suggestion.to, option.fieldDraft);
    }
    this.suggestion.set(null);
  }

  suggestionOptionId(index: number): string {
    return `mission-suggestion-${index}`;
  }

  private createEditor(document: AgentMissionDocument): void {
    const container = this.editorHost().nativeElement;
    this.quill = new Quill(container, {
      bounds: this.shell().nativeElement,
      formats: ['bold', 'italic', 'header', 'list', 'code-block', 'missionChip'],
      modules: {
        toolbar: false,
        history: { userOnly: true },
        clipboard: {
          matchers: [
            [Node.ELEMENT_NODE, (_node: Node, delta: Delta) => sanitizeMissionDelta(delta)],
          ],
        },
      },
      placeholder: 'Comece pela promessa operacional do agente.',
    });
    this.quill.root.addEventListener('keydown', this.keydownHandler, true);
    this.quill.on(Quill.events.TEXT_CHANGE, this.textChangeHandler);
    this.quill.on(Quill.events.SELECTION_CHANGE, this.selectionChangeHandler);
    this.configureKeyboard(this.quill);
    this.replaceDocument(document);
  }

  private replaceDocument(document: AgentMissionDocument): void {
    if (!this.quill) return;
    const previousSelection = this.quill.getSelection();
    const hadFocus = this.quill.hasFocus();
    const delta = missionDeltaFromDocument(document, this.mentionOptions());
    this.suppressTextChange = true;
    try {
      this.quill.setContents(sanitizeMissionDelta(delta), Quill.sources.SILENT);
    } finally {
      this.suppressTextChange = false;
    }
    if (previousSelection && hadFocus) {
      const maxIndex = Math.max(0, this.quill.getLength() - 1);
      this.quill.setSelection(
        Math.min(previousSelection.index, maxIndex),
        Math.min(previousSelection.length, maxIndex),
        Quill.sources.SILENT,
      );
      this.quill.focus();
    }
    this.lastDocumentText = missionDocumentText(document);
    this.lastMentionSignature = mentionOptionsSignature(this.mentionOptions());
  }

  private replaceRangeWithMention(from: number, to: number, mention: BuilderMissionMention): void {
    this.replaceRangeWithMissionChip(from, to, missionChipValueFromMention(mention));
  }

  private replaceRangeWithMissionChip(from: number, to: number, chip: MissionChipValue): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    this.suppressTextChange = true;
    try {
      quill.updateContents(
        new Delta()
          .retain(from)
          .delete(Math.max(0, to - from))
          .insert({ missionChip: chip })
          .insert(' '),
        Quill.sources.USER,
      );
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(from + 2, 0, Quill.sources.SILENT);
    quill.focus();
    this.emitSnapshot();
  }

  private replaceRangeWithStepSection(from: number, to: number): void {
    const quill = this.ensureQuill();
    if (!quill) return;
    const needsLeadingBreak = from > 0 && quill.getText(from - 1, 1) !== '\n';
    const needsTrailingBreak = to < quill.getLength() - 1 && quill.getText(to, 1) !== '\n';
    let sectionDelta = new Delta();
    if (needsLeadingBreak) sectionDelta = sectionDelta.insert('\n');
    sectionDelta = sectionDelta
      .insert('Nova etapa')
      .insert('\n', { header: 2 })
      .insert('Descreva objetivo, sinais de entrada, instruções e próximo passo desta etapa.')
      .insert('\n');
    if (needsTrailingBreak) sectionDelta = sectionDelta.insert('\n');
    this.suppressTextChange = true;
    try {
      quill.updateContents(
        new Delta()
          .retain(from)
          .delete(Math.max(0, to - from))
          .concat(sectionDelta),
        Quill.sources.USER,
      );
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(from + sectionDelta.length(), 0, Quill.sources.SILENT);
    quill.focus();
    this.emitSnapshot();
  }

  private emitSnapshot(): void {
    if (!this.quill) return;
    const snapshot = missionDocumentFromDelta(
      sanitizeMissionDelta(this.quill.getContents()),
      this.document().title || 'Missão do agente',
    );
    this.lastDocumentText = missionDocumentText(snapshot.document);
    this.lastMentionSignature = mentionOptionsSignature(this.mentionOptions());
    this.applyingExternalDocument = true;
    this.documentChange.emit(snapshot);
    queueMicrotask(() => {
      this.applyingExternalDocument = false;
    });
  }

  private replaceTrailingRawToken(): void {
    const quill = this.quill;
    const range = quill?.getSelection();
    if (!quill || !range || range.length > 0) return;
    const textBefore = quill.getText(0, range.index);
    const match = TYPED_RAW_TOKEN_PATTERN.exec(textBefore);
    if (!match) return;
    const raw = match[2] ?? '';
    const from = range.index - raw.length - 1;
    this.suppressTextChange = true;
    try {
      quill.updateContents(
        new Delta()
          .retain(from)
          .delete(raw.length)
          .insert({ missionChip: missionChipValueFromRawToken(raw) }),
        Quill.sources.USER,
      );
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(from + 2, 0, Quill.sources.SILENT);
  }

  private applyMarkdownShortcut(): void {
    const quill = this.quill;
    const range = quill?.getSelection();
    if (!quill || !range || range.length > 0) return;
    if (this.applyMarkdownLineShortcut(quill, range.index)) return;
    this.applyMarkdownInlineShortcut(quill, range.index);
  }

  private applyMarkdownLineShortcut(quill: Quill, index: number): boolean {
    const textBefore = quill.getText(0, index);
    const lineStart = textBefore.lastIndexOf('\n') + 1;
    const lineText = textBefore.slice(lineStart);
    const shortcut = markdownLineShortcut(lineText);
    if (!shortcut) return false;

    this.suppressTextChange = true;
    try {
      quill.updateContents(
        new Delta().retain(lineStart).delete(lineText.length),
        Quill.sources.USER,
      );
      quill.formatLine(lineStart, 1, shortcut.format, shortcut.value, Quill.sources.USER);
      if (shortcut.clearFormats.length > 0) {
        for (const format of shortcut.clearFormats) {
          quill.formatLine(lineStart, 1, format, false, Quill.sources.USER);
        }
      }
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(lineStart, 0, Quill.sources.SILENT);
    return true;
  }

  private applyMarkdownInlineShortcut(quill: Quill, index: number): void {
    const textBefore = quill.getText(0, index);
    const lineStart = textBefore.lastIndexOf('\n') + 1;
    const lineText = textBefore.slice(lineStart);
    const match = /(^|[\s([{])\*\*([^*\n][^\n]*?[^*\n]?)\*\*$/u.exec(lineText);
    if (!match) return;

    const raw = `**${match[2] ?? ''}**`;
    const label = match[2] ?? '';
    if (!label.trim()) return;
    const from = index - raw.length;

    this.suppressTextChange = true;
    try {
      quill.updateContents(
        new Delta().retain(from).delete(raw.length).insert(label, { bold: true }),
        Quill.sources.USER,
      );
    } finally {
      this.suppressTextChange = false;
    }
    quill.setSelection(from + label.length, 0, Quill.sources.SILENT);
  }

  private updateSuggestion(): void {
    const quill = this.quill;
    const range = quill?.getSelection();
    if (!quill || !range || range.length > 0) {
      this.suggestion.set(null);
      return;
    }

    const textBefore = quill.getText(0, range.index);
    const lineText = textBefore.slice(textBefore.lastIndexOf('\n') + 1);
    const trigger = missionCommandTriggerFromText(lineText);
    if (!trigger) {
      this.suggestion.set(null);
      return;
    }

    const options = mentionSuggestions(trigger, this.mentionOptions());
    const visibleOptions =
      options.length > 0 ? options : emptyMentionSuggestions(trigger.trigger, trigger.mentionTypes);
    if (visibleOptions.length === 0) {
      this.suggestion.set(null);
      return;
    }

    this.suggestion.set({
      from: range.index - trigger.fromOffset,
      to: range.index,
      ...this.suggestionPosition(quill, range.index),
      activeIndex: 0,
      options: visibleOptions,
    });
  }

  private suggestionPosition(
    quill: Quill,
    index: number,
  ): Pick<MissionSuggestionState, 'left' | 'top'> {
    const cursor = quill.getBounds(index);
    const shellBounds = this.shell().nativeElement.getBoundingClientRect();
    const hostBounds = this.editorHost().nativeElement.getBoundingClientRect();
    const maxLeft = Math.max(0, shellBounds.width - 368);
    return {
      left: Math.min(
        Math.max(0, hostBounds.left - shellBounds.left + (cursor?.left ?? 0)),
        maxLeft,
      ),
      top: hostBounds.top - shellBounds.top + (cursor?.bottom ?? 0) + 8,
    };
  }

  private handleSuggestionKeydown(event: KeyboardEvent): void {
    const suggestion = this.suggestion();
    if (!suggestion) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.suggestion.set(null);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const length = suggestion.options.length;
      this.suggestion.set({
        ...suggestion,
        activeIndex: (suggestion.activeIndex + direction + length) % length,
      });
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.suggestion.set({
        ...suggestion,
        activeIndex: event.key === 'Home' ? 0 : suggestion.options.length - 1,
      });
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const option = suggestion.options[suggestion.activeIndex] ?? suggestion.options[0];
      if (option && !option.disabled) this.applySuggestion(option);
    }
  }

  private configureKeyboard(quill: Quill): void {
    for (const key of ['>', '@', '!', '$']) {
      quill.keyboard.addBinding({ key, collapsed: true }, () => {
        queueMicrotask(() => this.updateSuggestion());
        return true;
      });
    }
  }

  private ensureQuill(): Quill | null {
    this.quill?.focus();
    return this.quill;
  }
}

function mentionSuggestions(
  trigger: NonNullable<ReturnType<typeof missionCommandTriggerFromText>>,
  mentions: readonly BuilderMissionMention[],
): readonly MissionSuggestionOption[] {
  const normalizedQuery = normalizeSuggestionQuery(trigger.query);
  const options = mentions
    .filter((mention) => trigger.mentionTypes.includes(mention.type))
    .filter(
      (mention) =>
        !normalizedQuery ||
        normalizeSuggestionText(`${mention.raw} ${mention.label} ${mention.tokenLabel}`).includes(
          normalizedQuery,
        ),
    )
    .slice(0, 8)
    .map((mention) => ({
      id: mention.raw,
      label: mention.label,
      tokenLabel: mention.tokenLabel,
      description: mention.description,
      mention,
    }));

  if (trigger.trigger !== '$') return options;
  const createOption = createFieldSuggestion(trigger.query, mentions);
  if (!createOption) return options;
  return [...options.slice(0, 7), createOption];
}

function emptyMentionSuggestions(
  trigger: '>' | '@' | '!' | '$',
  _types: readonly AgentMissionReference['type'][],
): readonly MissionSuggestionOption[] {
  if (trigger === '>') {
    return [
      emptySuggestion(
        '>',
        'Nenhuma automação disponível',
        'Crie uma automação ou conecte um fluxo para transferir a execução do agente.',
      ),
    ];
  }
  if (trigger === '@') {
    return [
      emptySuggestion(
        '@',
        'Nenhuma biblioteca ou skill selecionada',
        'Selecione fontes em Bibliotecas ou skills em Ferramentas para inserir referências.',
      ),
    ];
  }
  if (trigger === '!') {
    return [
      emptySuggestion(
        '!',
        'Nenhuma tool disponível',
        'Habilite tools em Ferramentas para inserir chamadas no fluxo conversacional.',
      ),
    ];
  }
  if (trigger === '$') {
    return [
      emptySuggestion(
        '$',
        'Digite uma variavel',
        'Use $telefone, $cpf ou outro nome curto para criar o campo no texto.',
      ),
    ];
  }
  return [];
}

function emptySuggestion(
  tokenLabel: string,
  label: string,
  description: string,
): MissionSuggestionOption {
  return {
    id: `empty-${tokenLabel}`,
    label,
    tokenLabel,
    description,
    disabled: true,
  };
}

function normalizeSuggestionQuery(value: string): string {
  return normalizeSuggestionText(
    value.replace(/^(library|skill|tool|trigger|automation|stage|handoff|field):/u, ''),
  );
}

function createFieldSuggestion(
  query: string,
  mentions: readonly BuilderMissionMention[],
): MissionSuggestionOption | null {
  const label = fieldLabelFromQuery(query);
  if (!label) return null;
  const key = fieldKeyFromLabel(label);
  const raw = `$field:${key}`;
  const normalizedLabel = normalizeSuggestionText(label);
  if (
    mentions.some(
      (mention) =>
        mention.type === 'field' &&
        (mention.raw === raw ||
          normalizeSuggestionText(mention.label) === normalizedLabel ||
          normalizeSuggestionText(mention.targetId.split('.').pop() || mention.targetId) ===
            normalizedLabel),
    )
  ) {
    return null;
  }
  return {
    id: `create-field:${key}`,
    label: `Criar variavel "${label}"`,
    tokenLabel: `$${label}`,
    description: 'O agente deve coletar essa informacao nesta etapa.',
    fieldDraft: {
      type: 'field',
      raw,
      targetId: key,
      label,
      tokenLabel: `$${label}`,
      description: 'Campo criado no texto da missao.',
    },
  };
}

function fieldLabelFromQuery(query: string): string {
  return query
    .replace(/^field:/u, '')
    .replace(/[._:-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./u, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function fieldKeyFromLabel(label: string): string {
  const slug =
    normalizeSuggestionText(label)
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'campo';
  return `conversation.${slug}`;
}

function normalizeSuggestionText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function mentionOptionsSignature(mentions: readonly BuilderMissionMention[]): string {
  return mentions
    .map((mention) => `${mention.raw}:${mention.tokenLabel}:${mention.description}`)
    .sort()
    .join('|');
}

type MarkdownLineShortcut = {
  format: 'header' | 'list' | 'code-block';
  value: number | string | boolean;
  clearFormats: readonly ('header' | 'list' | 'code-block')[];
};

function markdownLineShortcut(value: string): MarkdownLineShortcut | null {
  const shortcuts: Record<string, MarkdownLineShortcut> = {
    '# ': { format: 'header', value: 1, clearFormats: ['list', 'code-block'] },
    '## ': { format: 'header', value: 2, clearFormats: ['list', 'code-block'] },
    '### ': { format: 'header', value: 3, clearFormats: ['list', 'code-block'] },
    '- ': { format: 'list', value: 'bullet', clearFormats: ['header', 'code-block'] },
    '1. ': { format: 'list', value: 'ordered', clearFormats: ['header', 'code-block'] },
    '```': { format: 'code-block', value: true, clearFormats: ['header', 'list'] },
    '``` ': { format: 'code-block', value: true, clearFormats: ['header', 'list'] },
  };
  return shortcuts[value] ?? null;
}
